import React, { useMemo, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import { format } from "date-fns";
import { useSelector } from "react-redux";

import useGetRelationshipNoteDetails from "@fartherfinance/frontend/api/Notes/hooks/useGetRelationshipNoteDetails";
import useUpdateRelationshipNote from "@fartherfinance/frontend/api/Notes/hooks/useUpdateRelationshipNote";
import { RelationshipNoteId } from "@fartherfinance/frontend/api/Notes/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Textarea from "@src/sharedComponents/Textarea/Textarea";
import Text from "@src/sharedComponents/Typography/Text";
import { State } from "@src/store";

import styles from "./NoteForm.module.css";

interface NoteFormProps {
  noteId?: RelationshipNoteId; // For editing existing note - undefined means create mode
  clientId?: ClientId;
  onSave: (content: string) => void;
  onCancel: () => void;
  onDelete?: (noteId: RelationshipNoteId) => void;
  onUnsavedChangesWarning?: () => void;
  deletingNoteId?: boolean;
}

const NoteForm = ({
  noteId,
  clientId,
  onSave,
  onCancel,
  onDelete,
  onUnsavedChangesWarning,
  deletingNoteId = false,
}: NoteFormProps): JSX.Element => {
  const isEditing = noteId !== undefined;
  const { advisorId } = useSelector((state: State) => ({
    advisorId: state.main_Reducer.cockroach_advisor_id,
  }));
  const advisorAuth = useAdvisorRequestAuth();

  const { data: noteData, isLoading } = useGetRelationshipNoteDetails(
    noteId && advisorId
      ? {
          advisorId,
          noteId,
        }
      : null,
    advisorAuth,
    false,
    { enabled: !deletingNoteId && Boolean(noteId) && Boolean(advisorId) }
  );
  const updateNote = useUpdateRelationshipNote(advisorAuth);
  const showStatusNotification = useStatusNotification();

  const [content, setContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");

  // Update content when note data is loaded
  React.useEffect(() => {
    if (isEditing && noteData) {
      const noteContent = noteData.note || "";
      setContent(noteContent);
      setOriginalContent(noteContent);
    }
  }, [isEditing, noteData]);

  // Check if there are changes to enable Save button
  const hasChanges = isEditing
    ? content.trim() !== originalContent.trim() && content.trim().length > 0
    : content.trim().length > 0;

  const handleCancel = () => {
    if (hasChanges && onUnsavedChangesWarning) {
      onUnsavedChangesWarning();
    } else {
      onCancel();
    }
  };

  const updatedByText = useMemo(() => {
    if (!noteData) {
      return "";
    }

    return noteData.createdAt !== noteData.updatedAt
      ? `Last updated by ${noteData.updatedByName} on ${format(
          new Date(noteData.updatedAt),
          "P"
        )}`
      : `Created by ${noteData.updatedByName} on ${format(
          new Date(noteData.createdAt),
          "P"
        )}`;
  }, [noteData]);

  // Show loading state when editing and data is still loading
  if (isEditing && isLoading) {
    return (
      <div className={styles.container}>
        {/* Header with skeleton */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <IconButton
              IconComponent={ArrowBackIcon}
              onClick={handleCancel}
              style={{ height: 14, width: 14 }}
            />
            <Skeleton variant="text" width={200} height={20} />
            <Skeleton variant="circular" width={14} height={14} />
          </div>
          <div className={styles.lastUpdatedInfo}>
            <Skeleton variant="text" width={250} height={16} />
          </div>
        </div>

        {/* Content with skeleton textarea */}
        <div className={styles.content}>
          <div className={styles.textareaContainer}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              className={styles.textarea}
            />
          </div>
        </div>

        {/* Footer with skeleton buttons */}
        <div className={styles.footer}>
          <Skeleton variant="rectangular" width={80} height={36} />
          <Skeleton variant="rectangular" width={80} height={36} />
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    if (content.trim()) {
      if (isEditing && noteId && advisorId) {
        // Edit existing note
        await updateNote({
          advisorId,
          noteId: noteId,
          request: { note: content.trim() },
        });

        if (clientId && advisorId) {
          trackEvent({
            name: "Advisor Edit Client Note",
            properties: {
              clientId,
              advisorId,
              noteId,
            },
          });
        }

        // Update local state to reflect the changes
        setOriginalContent(content.trim());
        showStatusNotification("Note updated successfully", "Success");
        // Stay on the same page - don't call onCancel()
      } else {
        // Create new note
        onSave(content.trim());
      }
    }
  };

  const handleContentChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setContent(event.target.value);
  };

  const handleDeleteClick = () => {
    if (noteId && onDelete) {
      onDelete(noteId);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <IconButton
            IconComponent={ArrowBackIcon}
            onClick={handleCancel}
            style={{ height: 14, width: 14 }}
          />
          <Text variant="title3" color="text">
            {isEditing && noteData
              ? `Note created on ${format(new Date(noteData.createdAt), "P")}`
              : `New Note on ${format(new Date(), "P")}`}
          </Text>
          {/* Last updated info - only show when editing */}
          {isEditing && noteData && (
            <IconButton
              IconComponent={DeleteIcon}
              onClick={handleDeleteClick}
              style={{ height: 14, width: 14 }}
            />
          )}
        </div>
        {isEditing && noteData && (
          <div className={styles.lastUpdatedInfo}>
            <Text
              variant="body"
              color="textSubtle"
              style={{ textAlign: "center" }}
            >
              {updatedByText}
            </Text>
          </div>
        )}
      </div>

      {/* Form Content */}
      <div className={styles.content}>
        <Textarea
          value={content}
          onChange={handleContentChange}
          placeholder="Type your note here..."
          minRows={8}
          textareaClassName={styles.textarea}
          containerClassName={styles.textareaContainer}
          autoFocus
        />
      </div>

      {/* Footer Actions - Always show footer, but make buttons invisible when no changes in edit mode */}
      <div
        className={styles.footer}
        style={{
          visibility: !isEditing || hasChanges ? "visible" : "hidden",
        }}
      >
        <Button
          onClick={handleCancel}
          size="medium"
          variant="outlined"
          buttonType="secondary"
          text="Cancel"
        />
        <Button
          onClick={handleSave}
          size="medium"
          variant="contained"
          buttonType="primary"
          text="Save"
          disabled={!hasChanges}
        />
      </div>
    </div>
  );
};

export default NoteForm;
